// What Elariom shows, described as data: the connectors a workspace can use, and the
// workflows built so far. Statuses are read from the workspace and the server, never
// assumed, so a connector that is off shows as off.

import {
    faEnvelope, faShip, faPlane, faLink, faSparkles, faRobot, faCoins, faLocationDot, faCloud,
    faPaperPlane, faMapLocationDot, faServer, faInbox, faFileArrowUp, faCodeBranch, faCode,
    faUserCheck, faBookOpen, faDatabase, faFileLines, faCalendarClock, faBoxesStacked, faWebhook,
    faMagnifyingGlass, faFileContract, faGlobe,
} from '@fortawesome/pro-solid-svg-icons';
import { faWhatsapp, faSlack, faMicrosoft } from '@fortawesome/free-brands-svg-icons';

export const SUPER_ADMIN = 'admin@monada.ai';

// connected: working now. attention: switched on but something is missing.
// off: available to this workspace but not switched on. builtin: part of the platform,
// nothing to set up. planned: not built yet. unknown: the server did not say.
export const STATUS = {
    connected: { label: 'Connected', tone: 'good' },
    attention: { label: 'Needs attention', tone: 'warn' },
    off: { label: 'Off', tone: 'muted' },
    builtin: { label: 'Built in', tone: 'info' },
    planned: { label: 'Planned', tone: 'planned' },
    unknown: { label: 'Unknown', tone: 'muted' },
};

function hostOf(url) {
    try {
        return new URL(url).host;
    } catch (e) {
        return null;
    }
}

export function buildConnectors({ org, emailLoginInfo, platform, teams, whatsapp, accesstoken }) {
    const i = org?.integrations || {};
    const on = key => !!i[key]?.enabled;
    const serverKey = flag => (platform ? (flag ? 'connected' : 'attention') : 'unknown');

    const emailStatus = !on('email') ? 'off'
        : emailLoginInfo?.status === 'connected' ? 'connected'
            : emailLoginInfo?.status === 'expired' ? 'attention'
                : 'attention';
    const provider = i.email?.supplier === 'gmail' ? 'Gmail' : i.email?.supplier === 'outlook' ? 'Outlook' : null;

    const cf = i.cargofive || {};
    const cfStatus = !cf.enabled ? 'off' : cf.apiKey ? 'connected' : 'attention';
    const cfRouting = `FCL files: ${cf.sendFclImports === false ? 'Monada agent' : 'CargoFive'} · LCL files: ${cf.sendLclImports === false ? 'Monada agent' : 'CargoFive'}`;

    const cw = i.cargowise || {};
    const hook = i.opportunityWebhook || {};

    // This workspace's own Elariom in Teams (src/server/elariom/teams.js).
    const teamsStatus = !teams ? 'unknown'
        : !teams.configured ? 'attention'
            : !teams.connected ? 'off'
                : teams.lastError ? 'attention' : 'connected';
    const teamsDetail = !teams ? 'The server did not say.'
        : !teams.configured ? 'Elariom\'s Teams app details are not set on this server.'
            : !teams.connected ? `Not connected. Create this workspace's Teams account (elariom.${String(org?.title || org?.alias || 'workspace').toLowerCase().replace(/[^a-z0-9]+/g, '')}@monada.cloud, shown as "Elariom · ${org?.title || org?.alias || 'workspace'}"), then connect it here.`
                : [
                    `Signed in as ${teams.username}`,
                    teams.runsOn === teams.thisServer
                        ? (teams.polling ? `answering here: ${teams.answered || 0} answered, ${teams.refused || 0} turned away since this server started` : 'meant to run on this server, but its Teams loop is off (ELARIOM_TEAMS_POLL)')
                        : `answered by ${RUNS_ON_LABELS[teams.runsOn] || teams.runsOn}, not by this server (${RUNS_ON_LABELS[teams.thisServer] || teams.thisServer})`,
                    teams.lastError && `Last error: ${teams.lastError}`,
                ].filter(Boolean).join(' · ');

    return [
        {
            group: 'workspace',
            id: 'teams',
            name: 'Elariom in Teams',
            vendor: 'Microsoft Teams',
            icon: faMicrosoft,
            what: 'This workspace\'s own Elariom, a contact people add in Teams: no app to install. It answers only this workspace\'s users, and sees only this workspace\'s data.',
            status: teamsStatus,
            detail: teamsDetail,
            connect: teams?.configured && accesstoken && org?.id ? {
                href: `/api/elariom/teams/login?accesstoken=${accesstoken}&organizationId=${org.id}`,
                label: teams.connected ? 'Reconnect' : 'Connect',
                hint: 'Opens Microsoft\'s sign-in. Sign in as this workspace\'s Elariom account, not your own.',
            } : null,
            runsOn: teams?.connected ? teams.runsOn : null,
            usedBy: ['Inquiry to voyage completion'],
        },
        {
            group: 'workspace',
            id: 'whatsapp',
            name: 'Elariom in WhatsApp',
            vendor: 'WhatsApp · Meta',
            icon: faWhatsapp,
            what: 'This workspace\'s own WhatsApp number for Elariom. Same Elariom as in Teams; it answers only this workspace\'s users.',
            status: !whatsapp ? 'unknown' : !whatsapp.configured ? 'attention' : !whatsapp.connected ? 'off' : whatsapp.lastError ? 'attention' : 'connected',
            detail: !whatsapp ? 'The server did not say.'
                : !whatsapp.configured ? 'WhatsApp is not set up on this server (WHATSAPP_TOKEN, WHATSAPP_VERIFY_TOKEN).'
                    : !whatsapp.connected ? 'No number yet.'
                        : [whatsapp.number, whatsapp.name, `${whatsapp.people} ${whatsapp.people === 1 ? 'person' : 'people'} linked`, whatsapp.lastError && `Last error: ${whatsapp.lastError}`].filter(Boolean).join(' · '),
            whatsapp: whatsapp?.configured ? whatsapp : null,
            usedBy: ['Inquiry to voyage completion'],
        },
        {
            group: 'workspace',
            id: 'email',
            name: 'Email',
            vendor: provider ? `${provider} mailbox` : 'Gmail or Outlook',
            icon: faEnvelope,
            what: 'Reads and sends mail from each user\'s own mailbox: RFQs to suppliers, quotes to customers, replies, follow-ups.',
            status: emailStatus,
            detail: !on('email') ? 'Off for this workspace.'
                : emailLoginInfo?.status === 'connected' ? `On for the workspace. Your mailbox is connected${provider ? ` through ${provider}` : ''}.`
                    : emailLoginInfo?.status === 'expired' ? 'On for the workspace. Your mailbox sign-in has expired.'
                        : 'On for the workspace. Your own mailbox is not connected.',
            usedBy: ['Quotes', 'Follow-ups', 'Email'],
            settings: true,
        },
        {
            group: 'workspace',
            id: 'cargofive',
            name: 'CargoFive',
            vendor: 'Ocean rate platform',
            icon: faShip,
            what: 'Parses ocean rate files and searches ocean rates. Each workspace chooses, for FCL and for LCL, whether uploads go to CargoFive or to the Monada agent.',
            status: cfStatus,
            detail: cfStatus === 'off' ? 'Off for this workspace.' : cfStatus === 'attention' ? 'On, but no API key is saved.' : cfRouting,
            usedBy: ['Parse supplier rate files', 'Rates'],
            settings: true,
        },
        {
            group: 'workspace',
            id: 'cargowise',
            name: 'CargoWise',
            vendor: 'WiseTech TMS',
            icon: faBoxesStacked,
            what: 'Sends won quotes and shipments to CargoWise.',
            status: cw.enabled ? 'connected' : 'off',
            detail: cw.enabled ? `On, sending by ${(cw.method || 'xml').toUpperCase()}${cw.companies?.length ? ` for ${cw.companies.length} compan${cw.companies.length === 1 ? 'y' : 'ies'}` : ''}.` : 'Off for this workspace.',
            usedBy: ['Quotes', 'Bookings'],
            settings: true,
        },
        {
            group: 'workspace',
            id: 'okargo',
            name: 'Okargo',
            vendor: 'Spot ocean rates',
            icon: faMagnifyingGlass,
            what: 'Searches carriers\' spot ocean rates while quoting.',
            status: on('okargo') ? (i.okargo?.token ? 'connected' : 'attention') : 'off',
            detail: on('okargo') ? (i.okargo?.token ? 'On.' : 'On, but no token is saved.') : 'Off for this workspace.',
            usedBy: ['Rate search'],
            settings: true,
        },
        {
            group: 'workspace',
            id: 'webcargo',
            name: 'WebCargo',
            vendor: 'Air rates',
            icon: faPlane,
            what: 'Searches airline rates while quoting air.',
            status: on('webcargo') ? 'connected' : 'off',
            detail: on('webcargo') ? 'On.' : 'Off for this workspace.',
            usedBy: ['Rate search'],
            settings: true,
        },
        {
            group: 'workspace',
            id: 'cargoai',
            name: 'CargoAI',
            vendor: 'Air rates',
            icon: faPlane,
            what: 'Searches air rates while quoting air.',
            status: on('cargoai') ? 'connected' : 'off',
            detail: on('cargoai') ? 'On.' : 'Off for this workspace.',
            usedBy: ['Rate search'],
            settings: true,
        },
        {
            group: 'workspace',
            id: 'webhook',
            name: 'Quote webhook',
            vendor: 'Outbound HTTP',
            icon: faWebhook,
            what: 'Posts quote events to a URL the customer owns.',
            status: hook.enabled ? (hook.url ? 'connected' : 'attention') : 'off',
            detail: hook.enabled ? (hook.url ? `Posting to ${hostOf(hook.url) || hook.url}${hook.events?.length ? `, ${hook.events.length} event${hook.events.length === 1 ? '' : 's'}` : ''}.` : 'On, but no URL is saved.') : 'Off for this workspace.',
            usedBy: ['Quotes'],
            settings: true,
        },

        {
            group: 'platform',
            id: 'claude-agent',
            name: 'Claude',
            vendor: 'Anthropic · rate agent',
            icon: faSparkles,
            what: 'The model behind the agent steps: classifying a rate file and parsing its rates.',
            status: serverKey(platform?.rateAgentKey),
            detail: !platform ? 'The server did not report its keys.' : platform.rateAgentKey ? `Key set on the server. Model: ${platform.rateAgentModel}.` : 'No RATE_AGENT_ANTHROPIC_API_KEY on the server.',
            usedBy: ['Parse supplier rate files'],
        },
        {
            group: 'platform',
            id: 'claude-help',
            name: 'Claude',
            vendor: 'Anthropic · help assistant',
            icon: faSparkles,
            what: 'Answers users\' questions about Monada in the help panel.',
            status: serverKey(platform?.helpAssistantKey),
            detail: !platform ? 'The server did not report its keys.' : platform.helpAssistantKey ? 'Key set on the server.' : 'No ANTHROPIC_API_KEY on the server.',
            usedBy: ['Help assistant'],
        },
        {
            group: 'platform',
            id: 'openai',
            name: 'OpenAI',
            vendor: 'GPT models',
            icon: faRobot,
            what: 'Summaries and extraction in email conversations, accounts, suppliers and reports.',
            status: serverKey(platform?.openaiKey),
            detail: !platform ? 'The server did not report its keys.' : platform.openaiKey ? 'Key set on the server.' : 'No OPENAI_API_KEY on the server.',
            usedBy: ['Email', 'Accounts', 'Reports'],
        },
        {
            group: 'platform',
            id: 'places',
            name: 'Places data',
            vendor: 'UN/LOCODE + airports',
            icon: faLocationDot,
            what: 'Every port, town and airport code. The rate agent looks places up here, so a code it writes is one the importer accepts.',
            status: 'builtin',
            detail: 'UN/LOCODE list in file storage, airports bundled with the app, custom ports per workspace.',
            usedBy: ['Parse supplier rate files', 'Quotes', 'Rates'],
        },
        {
            group: 'platform',
            id: 'fx',
            name: 'Exchange rates',
            vendor: 'fixer.io',
            icon: faCoins,
            what: 'Live currency rates, refreshed every 6 hours. A workspace can override them by hand.',
            status: 'builtin',
            detail: org?.exchangeRates && Object.keys(org.exchangeRates).length ? 'Live rates, with manual overrides set on this workspace.' : 'Live rates.',
            usedBy: ['Quotes', 'Analytics'],
        },
        {
            group: 'platform',
            id: 'storage',
            name: 'File storage',
            vendor: 'Amazon S3',
            icon: faCloud,
            what: 'Uploaded rate files, attachments, and every agent run\'s full result.',
            status: 'builtin',
            detail: 'Bucket monada-storage.',
            usedBy: ['Parse supplier rate files', 'Quotes'],
        },
        {
            group: 'platform',
            id: 'database',
            name: 'Monada data',
            vendor: 'DynamoDB',
            icon: faDatabase,
            what: 'Quotes, rates, suppliers, accounts, bookings: the records every workflow reads and writes.',
            status: 'builtin',
            detail: 'Shared by every app on this workspace.',
            usedBy: ['Everything'],
        },
        {
            group: 'platform',
            id: 'system-mail',
            name: 'System email',
            vendor: 'SendGrid',
            icon: faPaperPlane,
            what: 'Sign-in, password and notification mail sent by Monada itself.',
            status: 'builtin',
            detail: 'Global, not per workspace.',
            usedBy: ['Notifications'],
        },
        {
            group: 'platform',
            id: 'maps',
            name: 'Maps',
            vendor: 'Google Places',
            icon: faMapLocationDot,
            what: 'Address search for door pickups and deliveries.',
            status: 'builtin',
            detail: 'Global, not per workspace.',
            usedBy: ['Quotes'],
        },

        {
            group: 'planned',
            id: 'rate-inbox',
            name: 'Rate file inbox',
            vendor: 'Email in',
            icon: faInbox,
            what: 'A mailbox suppliers send tariffs to. Each attachment starts the rate-parsing workflow, with no upload by hand.',
            status: 'planned',
            detail: 'Next connector for the rate-parsing workflow.',
            usedBy: ['Parse supplier rate files'],
        },
        {
            group: 'planned',
            id: 'slack',
            name: 'Slack',
            vendor: 'Slack',
            icon: faSlack,
            what: 'Alerts and approvals inside Slack channels.',
            status: 'planned',
            usedBy: [],
        },
        {
            group: 'planned',
            id: 'calendar',
            name: 'Calendar',
            vendor: 'Google or Outlook',
            icon: faCalendarClock,
            what: 'Meetings and reminders, so the assistant knows the day and can book time.',
            status: 'planned',
            usedBy: [],
        },
        {
            group: 'planned',
            id: 'mcp',
            name: 'MCP server',
            vendor: 'Any',
            icon: faServer,
            what: 'Connect any system that speaks the Model Context Protocol, and give its tools to agents.',
            status: 'planned',
            usedBy: [],
        },
    ];
}

// Which server answers a workspace's Elariom (src/server/elariom/teams.js · RUNS_ON).
export const RUNS_ON_LABELS = { production: 'Production', alpha: 'ALPHA', local: 'Local (developer)' };

export const CONNECTOR_GROUPS = [
    { id: 'workspace', title: 'This workspace', blurb: 'Switched on and set up per workspace, in Settings → Integrations.' },
    { id: 'platform', title: 'Platform services', blurb: 'Shared by every workspace. Set up on the server.' },
    { id: 'planned', title: 'Planned', blurb: 'Not built yet.' },
];

// Kinds of step a workflow is made of, with the color each is drawn in on the canvas.
export const NODE_KINDS = {
    // Elariom is monochrome: a step's kind shows in its label and icon, not in colour. Steps
    // outside the flow (an external service, the knowledge it feeds) are drawn in grey.
    trigger: { label: 'Trigger', icon: faFileArrowUp, color: '#1C1B19' },
    condition: { label: 'Condition', icon: faCodeBranch, color: '#1C1B19' },
    code: { label: 'Code', icon: faCode, color: '#1C1B19' },
    agent: { label: 'Agent', icon: faSparkles, color: '#1C1B19' },
    human: { label: 'Human approval', icon: faUserCheck, color: '#1C1B19' },
    action: { label: 'Action', icon: faDatabase, color: '#1C1B19' },
    external: { label: 'External service', icon: faLink, color: '#6F6B63' },
    knowledge: { label: 'Knowledge', icon: faBookOpen, color: '#6F6B63' },
};

export const TRIGGERS = [
    { title: 'File or email dropped', detail: 'Drop a file, or an email saved as .eml, onto the workflow.', built: true },
    { title: 'File uploaded', detail: 'A user uploads a file in an app, e.g. Rates → Import.', built: true },
    { title: 'Email received', detail: 'A message with an attachment arrives in the workspace\'s rates inbox or a user\'s mailbox.', built: false },
    { title: 'Record changed', detail: 'A quote, rate, booking or account is created or updated.', built: false },
    { title: 'Schedule', detail: 'Every day, week, or at a set time.', built: false },
    { title: 'Webhook', detail: 'Another system calls a URL.', built: false },
];

export const TOOLS = [
    { name: 'lookup_locations', detail: 'Find UN/LOCODE and airport codes for places named in a file.', built: true },
    { name: 'create_rate_form', detail: 'Create a supplier rate form from the charges found.', built: false },
    { name: 'publish_rates', detail: 'Publish reviewed rates into the workspace.', built: false },
    { name: 'search_quotes', detail: 'Find quotes by customer, lane, status or date.', built: false },
    { name: 'send_email', detail: 'Send from the user\'s connected mailbox.', built: false },
];

// The rate-parsing workflow as it runs today (src/server/rateAgent, admin agent runs).
// x/y place each step on the canvas; a step not built yet is drawn dashed.
export function rateParsingWorkflow({ oceanRouting, knowledge, platform }) {
    const model = platform?.rateAgentModel || 'claude-sonnet-5';
    const classifyModel = platform?.rateAgentClassifyModel || 'claude-haiku-4-5-20251001';
    const categories = knowledge?.categories?.length || 8;
    const fcl = oceanRouting?.fcl === 'monada' ? 'Monada agent' : 'CargoFive';
    const lcl = oceanRouting?.lcl === 'monada' ? 'Monada agent' : 'CargoFive';
    const cargofiveUsed = oceanRouting?.fcl !== 'monada' || oceanRouting?.lcl !== 'monada';
    const W = 200;
    const GAP = 52;
    const col = n => 24 + n * (W + GAP);
    const MAIN = 80;
    const ROW2 = 250;
    const ROW3 = 400;
    const ROW4 = 550;

    let nodes = [
        {
            id: 'email', kind: 'trigger', icon: faEnvelope, x: col(0), y: 16, planned: true,
            title: 'Email received', subtitle: 'Workspace or user mailbox',
            what: 'An email with a rate file attached arrives in a connected mailbox: the workspace\'s own rates inbox, or a user\'s mailbox. The workflow picks it up without anyone uploading anything.',
            where: 'Planned: the Rate file inbox and Email connectors',
        },
        {
            id: 'drop', kind: 'trigger', x: col(0), y: 144,
            title: 'File or email dropped', subtitle: 'PDF, Excel, CSV or .eml',
            what: 'Someone drops the rate file itself, or the supplier\'s email saved from Outlook as .eml, onto this workflow. (Customers still upload through Rates → Import, which follows the same steps.)',
            where: 'Elariom → Try it; POST /api/admin/elariom/:workspace/drops',
            config: ['PDF, XLSX, XLS, CSV', '.eml from Outlook'],
        },
        {
            id: 'fetch', kind: 'code', x: col(1), y: MAIN,
            title: 'Get the rate file', subtitle: 'From the email, or as dropped',
            what: 'From an email, takes out the PDF, Excel and CSV attachments and passes the subject and body on as the supplier\'s notes. A dropped file is used as it is. The run form is filled in from what the file\'s columns state (mode, supplier, validity).',
            where: 'admin.js · Elariom drops (mailparser); fileChecks.js · fileHints',
        },
        {
            id: 'route', kind: 'condition', x: col(2), y: MAIN,
            title: 'Who parses it?', subtitle: `FCL → ${fcl} · LCL → ${lcl}`,
            what: 'Air and land files always stay in Monada. Ocean files follow this workspace\'s switch, one for FCL and one for LCL. Files dropped here always stay in Monada, so any file can be tried.',
            where: 'integrations.cargofive.sendFclImports / sendLclImports',
            config: [`FCL: ${fcl}`, `LCL: ${lcl}`, 'Air, land: Monada'],
        },
        {
            id: 'known', kind: 'condition', x: col(3), y: MAIN,
            title: 'Known format?', subtitle: 'Script or converter, no model',
            what: 'Files in a layout Monada already knows are converted by code, with no model and no tokens: a Monada import CSV, an air rate table with weight breaks, a metro trucking sheet, an LCL tariff with CBM bands.',
            where: 'fileChecks.js · checkMonadaCsv; converters.js',
        },
        {
            id: 'tables', kind: 'code', x: col(4), y: MAIN,
            title: 'Read as tables', subtitle: 'Sheets as they are · PDF rebuilt',
            what: 'Turns the file into plain tables. A spreadsheet or CSV is read as it is. A PDF is rebuilt into rows and columns from where each piece of text sits on the page; a scanned PDF has no text and goes to the full parser.',
            where: 'tables.js · sheetTables, pdfTables (pdfjs-dist)',
        },
        {
            id: 'classify', kind: 'agent', x: col(5), y: MAIN,
            title: 'Classify', subtitle: `Agent · ${classifyModel}`,
            what: `Decides which of the ${categories} kinds of rate file this is (air freight, LCL drayage, FCL freight, road…). A mode someone gave is kept; with none given it decides the mode and names the supplier from the file and the email. Runs on the cheapest model: one short answer about the whole file.`,
            where: 'agent.js · classify()',
            config: [`Knowledge: classification rules${knowledge?.classificationRuleCount ? ` (${knowledge.classificationRuleCount})` : ''}`, `${categories} categories`, `Model: ${classifyModel}`],
        },
        {
            id: 'savedRecipe', kind: 'condition', x: col(6), y: MAIN,
            title: 'Saved recipe?', subtitle: 'Same layout seen before',
            what: 'If a recipe was saved for this exact table layout (the same column titles), it is used as it is: no model reads the rates at all. A recipe is saved when rates read with it are reviewed and published.',
            where: 'S3 rate-agent/recipes/<workspace>/<layout>.json',
        },
        {
            id: 'recipe', kind: 'agent', x: col(7), y: MAIN,
            title: 'Write recipe', subtitle: `Agent · ${model}`,
            what: 'The agent reads the table once and writes a recipe instead of the rates: which column holds the origin, destination, validity and currency; how the price is built (weight breaks, a price per container size, one price per row, or W/M); the other charges; and the notes for every rate. A short answer, the same size for 50 rows or 5,000. If the rates are not a regular table, it says so and the full parser reads the file.',
            where: 'agent.js · writeRecipe(); recipe.js · RECIPE_TOOL',
        },
        {
            id: 'apply', kind: 'code', x: col(8), y: MAIN,
            title: 'Apply recipe', subtitle: 'Every row, exact, no tokens',
            what: 'Code applies the recipe to every row: places by code, or looked up by name in Monada\'s ports, UN/LOCODE and airport lists; dates in one order for the file; the price built from the weight breaks, so a shipment just under a break is never charged more than the next break would cost. Rows whose prices look shifted by a column are set aside with the reason. Then the rate form and the Monada CSV.',
            where: 'recipe.js · applyRecipe(); the form: agent.js · buildForm(), sections from the charge glossary',
        },
        {
            id: 'places', kind: 'code', x: col(9), y: MAIN,
            title: 'Resolve places', subtitle: 'Codes and names Monada doesn\'t know',
            what: 'Every origin and destination read from the file, for air, sea and road: a cell with several codes or names becomes one rate line per place; Monada\'s ports, UN/LOCODEs and open airports are kept; a city code (OSA, TYO, LON…) becomes one rate per airport; anything still unknown is asked of Claude, with the cell and the place name the file gives. An answer is used only if Monada\'s lists have it and it is in the same country as the codes beside it. Answers are remembered for every workspace.',
            where: 'placeResolver.js; agent.js · searchPlaceCodes (Sonnet, about 1,100 tokens a lookup); S3 rate-agent/place-aliases.json',
            config: ['Air, sea and road', 'Remembered once found', 'Checked against Monada\'s lists'],
        },
        {
            id: 'validate', kind: 'code', x: col(10), y: MAIN,
            title: 'Check with importer', subtitle: 'Same code as a real import · draft form',
            what: 'Runs the result through the real importer, so a row it would reject is flagged before anyone reviews it. The rate form is drafted by here, with its charges in Origin, Freight and Destination sections from the charge glossary: a script\'s form is drafted in this check from the file\'s charge columns; the agent\'s form was built when the recipe was applied or the rates parsed. Review shows the draft and the reviewer can change a charge\'s type or section.',
            where: 'fileChecks.js · checkMonadaCsv() (scripts); agent.js · buildForm() and validate() (agent)',
            config: ['Draft form: Origin · Freight · Destination', 'Sections: charge glossary'],
        },
        {
            id: 'review', kind: 'human', x: col(11), y: MAIN,
            title: 'Review & edit', subtitle: 'Correct cells, leave rows out',
            what: 'A person sees the rates, corrects cells, leaves rows out, decides columns that don\'t match the form, and what happens to older versions of these rates. A new form\'s charges are shown in their Origin, Freight and Destination sections, each with its price type; either can be changed. The recipe is shown, so it is clear how each column was read.',
            where: 'Try it → a run → Review & publish (ElariomReview.js, ElariomReviewRates.js)',
        },
        {
            id: 'publish', kind: 'action', x: col(12), y: MAIN,
            title: 'Publish rates', subtitle: 'Into this workspace · can be undone',
            what: 'Creates the rate form and the approved rates in this workspace; ends, deletes or keeps older versions as chosen; and saves a newly written recipe for this layout. The new form is saved as reviewed, in the Origin, Freight and Destination sections it was drafted with; charges added to an existing form go to their section. Unpublish removes the batch and puts older rates back.',
            where: 'POST forms, /api/rates/bulk; existing-rates/apply; recipe saved on publish',
            config: ['Form saved as reviewed', 'Older rates: end, delete or keep', 'Recipe saved for the layout'],
        },
        {
            id: 'cargofive', kind: 'external', x: col(2), y: ROW2,
            inactive: !cargofiveUsed,
            title: 'CargoFive', subtitle: cargofiveUsed ? `On: parses ${[oceanRouting?.fcl !== 'monada' && 'FCL', oceanRouting?.lcl !== 'monada' && 'LCL'].filter(Boolean).join(' and ')} files` : 'Off: not used on this workspace',
            what: cargofiveUsed
                ? 'Ocean files routed to CargoFive are parsed there and come back through the CargoFive sync. They leave this workflow here.'
                : 'Not used: this workspace sends both FCL and LCL files to the Monada agent. Use the switch on this step to route them to CargoFive again.',
            where: 'src/server/api/cargofive.js',
        },
        {
            id: 'script', kind: 'code', x: col(3), y: ROW2,
            title: 'Script or converter', subtitle: 'Known layouts, no model',
            what: 'Monada CSV: checked as the importer reads it, dates and codes fixed, columns matched to the form. Air rate table: one rate per row, weight breaks built into one formula. CMA CGM quotation export: rates from Standard charges, checked against the Summary, feeder routes built. Metro trucking sheets and LCL tariffs with CBM bands: Monada\'s own converters. A script returns charges named as the carrier names them, never form sections: the form is drafted in sections by the charge glossary in the importer check, before review.',
            where: 'fileChecks.js, converters.js',
        },
        {
            id: 'parse', kind: 'agent', x: col(7), y: ROW3,
            title: 'Parse rates (full)', subtitle: `Agent · ${model} · in batches`,
            what: 'For files that need reading: scanned PDFs, prose, irregular layouts. The agent plans the charges once, then writes the rates in batches, each reading the whole file from the cache; code joins them. A place it can\'t find is written as the file writes it and settled in Resolve places, like every other path.',
            where: 'agent.js · parseInBatches()',
            config: ['Tool: lookup_locations', 'Knowledge: the rule pack for the category', 'Form sections: charge glossary'],
        },
        {
            id: 'glossary', kind: 'knowledge', x: col(10), y: ROW2,
            title: 'Charge glossary', subtitle: 'Origin · Freight · Destination',
            what: 'Which section of the rate form each charge goes in, learned from CargoFive\'s sections for 137,000 charges (143 charge names), with rules for names it never saw: the carrier\'s own transport and security is Freight, a side named in the charge decides ("Origin THC", "Import fee"), then the kind of charge. Every rate form is drafted in these sections before review, whether a script or the agent read the file; the reviewer can move a charge, and publish saves the form as reviewed.',
            where: 'chargeGlossary.js · docs/air-agent/charge-glossary.md · rule R11',
            config: ['143 charge names from CargoFive', 'Used by scripts and the agent', 'Agents\' rule: R11'],
        },
        {
            id: 'gaps', kind: 'knowledge', x: col(7), y: ROW4,
            title: 'Gaps & corrections', subtitle: 'Feed the rule book',
            what: 'What the rules did not cover, and what reviewers corrected, are kept with the run. Resolved gaps become new rule versions.',
            where: 'Admin → Rate imports → Gaps & corrections',
        },
    ];

    let edges = [
        { from: 'email', to: 'fetch' },
        { from: 'drop', to: 'fetch' },
        { from: 'fetch', to: 'route' },
        { from: 'route', to: 'known', label: 'Monada' },
        { from: 'route', to: 'cargofive', label: 'CargoFive' },
        { from: 'known', to: 'tables', label: 'no' },
        { from: 'known', to: 'script', label: 'yes' },
        { from: 'script', to: 'places', route: 'back' },
        { from: 'tables', to: 'classify' },
        { from: 'classify', to: 'savedRecipe' },
        { from: 'savedRecipe', to: 'recipe', label: 'no' },
        { from: 'savedRecipe', to: 'apply', label: 'yes', route: 'over' },
        { from: 'recipe', to: 'apply', label: 'a table' },
        { from: 'recipe', to: 'parse', label: 'not a table' },
        { from: 'parse', to: 'places', route: 'back' },
        { from: 'parse', to: 'gaps', label: 'gaps' },
        { from: 'apply', to: 'places' },
        { from: 'places', to: 'validate' },
        { from: 'validate', to: 'review' },
        { from: 'review', to: 'publish', label: 'approved' },
        { from: 'glossary', to: 'validate', label: 'form sections', route: 'up' },
    ].map(e => ({ label: null, route: 'straight', ...e }));

    // With CargoFive off, nothing is routed: the condition leaves the flow, and CargoFive stays
    // only as a switched-off box (holding its switch) under Get the rate file.
    let width = col(13);
    if (!cargofiveUsed) {
        nodes = nodes
            .filter(n => n.id !== 'route')
            .map(n => (n.id === 'cargofive' ? { ...n, x: col(1) } : n.x > col(2) ? { ...n, x: n.x - (W + GAP) } : n));
        edges = [{ from: 'fetch', to: 'known', label: null, route: 'straight' }, ...edges.filter(e => e.from !== 'route' && e.to !== 'route')];
        width = col(12);
    }

    return {
        id: 'rate-parsing',
        name: 'Parse supplier rate files',
        description: 'Turns a supplier\'s rate file into a rate form and rates ready to publish: by code when the layout is known or a recipe fits, by the agent only when the file needs reading.',
        trigger: 'Email received, or a file or email dropped',
        width,
        height: ROW4 + 120,
        nodeWidth: W,
        nodes,
        edges,
    };
}

// Workflows #2 and #3, shown to customers as previews (docs/air-agent/platform-plan.md; their
// build status is kept there, not here). #2 ends when the customer accepts; #3 takes it from there. state: 'built' or 'partial' steps
// are available today, 'planned' ones are coming soon.
export function inquiryToAcceptedQuoteWorkflow() {
    const W = 200;
    const GAP = 52;
    const col = n => 24 + n * (W + GAP);
    const MAIN = 80;
    const ROW2 = 250;
    const ROW3 = 400;

    const nodes = [
        {
            id: 'email', kind: 'trigger', icon: faEnvelope, x: col(0), y: 16, state: 'planned',
            title: 'Email arrives', subtitle: 'Company inbox or a sales person\'s mailbox',
            what: 'Every email that reaches a connected mailbox: the company inbox, and the personal mailboxes of the sales people, because their leads write to them directly.',
        },
        {
            id: 'chat', kind: 'trigger', icon: faMicrosoft, x: col(0), y: 144, state: 'partial',
            title: 'Message to Elariom', subtitle: 'In Teams, or forwarded by email',
            what: 'Write to Elariom in Teams, or forward it an email. Nothing to install: Elariom is a contact your team adds in Teams, and it works only for your company\'s team.',
        },
        {
            id: 'triage', kind: 'agent', x: col(1), y: MAIN, state: 'planned',
            title: 'Triage', subtitle: 'What is it, and which job is it about?',
            what: 'Decides what each email is: a new inquiry, a message on something already running (a quote, booking, shipment or invoice), a supplier\'s rate file, a reply to one of our RFQs, other business, or not business. Sets a priority. Attaches it to the job it belongs to, or opens a new one.',
        },
        {
            id: 'others', kind: 'action', x: col(1), y: ROW2, state: 'partial',
            title: 'Hand over', subtitle: 'To the workflow it belongs to',
            what: 'An email that isn\'t a new inquiry goes where it belongs: a supplier\'s rate file to Parse supplier rate files, an RFQ reply to the RFQ step of its job, a message on a booking to that booking.',
        },
        {
            id: 'job', kind: 'knowledge', x: col(2), y: ROW3, state: 'planned',
            title: 'The job', subtitle: 'Every thread and record of one shipment',
            what: 'Everything about one shipment in one place: the customer\'s emails, the requests to agents and their replies, the booking, the arrival notice, the quote and the shipment, even when they arrived in different email threads.',
        },
        {
            id: 'intake', kind: 'agent', x: col(2), y: MAIN, state: 'partial',
            title: 'Understand the inquiry', subtitle: 'Route, cargo, dates, Incoterm, services',
            what: 'Breaks the inquiry into its parts and creates the quote. Checks what is missing for pricing (DG without an MSDS, reefer without a temperature, EXW without a pick-up address). Resolves "price me again what I wanted last time" to the customer\'s recent quotes and shipments.',
        },
        {
            id: 'ready', kind: 'condition', x: col(3), y: MAIN, state: 'planned',
            title: 'Ready for pricing?', subtitle: 'And the pricing plan',
            what: 'When nothing is missing, declares the inquiry ready and writes the pricing plan: which legs the quote needs, and for each leg whether to search rates, ask agents or suppliers, or both. Imports usually go to the overseas agent; domestic legs to contract rates or local suppliers; the Incoterm decides which legs exist.',
        },
        {
            id: 'ask', kind: 'human', x: col(3), y: ROW2, state: 'planned',
            title: 'Ask the customer', subtitle: 'Follow-up questions, approved first',
            what: 'Drafts the questions for what is missing, in the customer\'s language. The user approves before it goes out; the workflow waits for the answer and continues.',
        },
        {
            id: 'search', kind: 'code', icon: faMagnifyingGlass, x: col(4), y: MAIN, state: 'partial',
            title: 'Search rates', subtitle: 'Rates, spot offers, Okargo',
            what: 'Finds costs for each leg of the shipment: your own rates in Monada, recent offers from your agents and suppliers, and live offers from carriers\' platforms. Every price shows where it came from, so you know how much to trust it.',
        },
        {
            id: 'sources', kind: 'external', x: col(4), y: ROW2, state: 'partial',
            title: 'Rates and spot platforms', subtitle: 'Monada rates · Okargo · CargoFive · CargoAI',
            what: 'Where Elariom looks for prices: your published rates in Monada and the rate platforms connected to your workspace (Okargo, CargoFive, CargoAI).',
        },
        {
            id: 'portals', kind: 'external', icon: faGlobe, x: col(4), y: ROW3, state: 'planned',
            title: 'Carrier websites', subtitle: 'With your company\'s own logins',
            what: 'For equipment or carriers the rate platforms don\'t cover, such as open top, flat rack or some reefer and dangerous cargo, Elariom looks up prices on the carrier\'s own website with your company\'s login, the way your team would.',
        },
        {
            id: 'rfq', kind: 'agent', x: col(5), y: MAIN, state: 'partial',
            title: 'Ask agents & suppliers', subtitle: 'RFQs, follow-ups, replies read',
            what: 'For legs without a good price: chooses who to ask, drafts each RFQ (complete, with urgency, rephrased so the customer isn\'t revealed), sends after approval, reads the replies, follows up with whoever hasn\'t answered and tells the user when replies arrive.',
        },
        {
            id: 'suppliers', kind: 'external', x: col(5), y: ROW2, state: 'partial',
            title: 'Agents & suppliers', subtitle: 'Email · WhatsApp groups later',
            what: 'Overseas agents, carriers and local suppliers, reached by email; later, the user\'s WhatsApp groups (always approved, never revealing the customer or prices).',
        },
        {
            id: 'collector', kind: 'agent', x: col(6), y: MAIN, state: 'partial',
            title: 'Collect & compare', subtitle: 'Options, totals, margin, draft reply',
            what: 'Combines the legs into options: total, breakdown, route, transit time, carrier and supplier. Says who answered and who is still open, and suggests sending what is ready rather than waiting for everyone. Applies your margin, shows cost, margin and sell, and drafts the reply to the customer with sell prices only.',
        },
        {
            id: 'approve', kind: 'human', x: col(7), y: MAIN, state: 'partial',
            title: 'Approve & send', subtitle: 'From the mailbox the customer wrote to',
            what: 'The user approves, edits or tells Elariom what to change, in Teams. The reply goes out from Monada, in the same email thread and from the mailbox the customer wrote to (often the sales person\'s own).',
        },
        {
            id: 'teams', kind: 'external', icon: faMicrosoft, x: col(7), y: ROW2, state: 'partial',
            title: 'Elariom in Teams', subtitle: 'Where your team talks to it',
            what: 'Progress, questions, approvals and the comparison of options happen in your Teams chat with Elariom. Monada shows the whole job.',
        },
        {
            id: 'followup', kind: 'agent', x: col(8), y: MAIN, state: 'partial',
            title: 'Customer follow-up', subtitle: 'Reply, questions, silence',
            what: 'Watches the thread after the quote is sent: tells the user when the customer answers, helps with their questions, follows up when they go quiet, and recognises acceptance.',
        },
        {
            id: 'accepted', kind: 'action', x: col(9), y: MAIN, state: 'planned',
            title: 'Quote accepted', subtitle: 'Starts Booking to voyage completion',
            what: 'When the customer accepts, the job moves on to the Booking to voyage completion workflow, with everything agreed: the option, the price, the customer\'s details and the emails so far.',
        },
    ].map(n => ({ ...n, planned: n.state === 'planned' }));

    const edges = [
        { from: 'email', to: 'triage' },
        { from: 'chat', to: 'triage' },
        { from: 'triage', to: 'intake', label: 'new inquiry' },
        { from: 'triage', to: 'others', label: 'anything else' },
        { from: 'triage', to: 'job' },
        { from: 'intake', to: 'ready' },
        { from: 'ready', to: 'ask', label: 'missing' },
        { from: 'ask', to: 'intake', label: 'answers', route: 'left' },
        { from: 'ready', to: 'search', label: 'ready' },
        { from: 'search', to: 'sources' },
        { from: 'sources', to: 'portals', label: 'not covered' },
        { from: 'search', to: 'rfq', label: 'no price' },
        { from: 'search', to: 'collector', label: 'priced legs', route: 'over' },
        { from: 'rfq', to: 'suppliers' },
        { from: 'rfq', to: 'collector', label: 'replies' },
        { from: 'collector', to: 'approve' },
        { from: 'approve', to: 'teams' },
        { from: 'approve', to: 'followup', label: 'sent' },
        { from: 'followup', to: 'accepted', label: 'accepted' },
        { from: 'followup', to: 'approve', label: 'reply or questions', route: 'under' },
    ].map(e => ({ label: null, route: 'straight', ...e }));

    return {
        id: 'inquiry-to-accepted-quote',
        name: 'Inquiry to accepted quote',
        preview: true,
        description: 'From a customer\'s email to an accepted quote: understands the inquiry, asks for what\'s missing, finds costs and asks agents, compares options, drafts the reply for approval, and follows up until the customer accepts.',
        trigger: 'Email arrives, or a message to Elariom in Teams',
        width: col(10),
        height: ROW3 + 120,
        nodeWidth: W,
        nodes,
        edges,
    };
}

export function bookingToVoyageWorkflow() {
    const W = 200;
    const GAP = 52;
    const col = n => 24 + n * (W + GAP);
    const MAIN = 80;
    const ROW2 = 250;

    const nodes = [
        {
            id: 'accepted', kind: 'trigger', x: col(0), y: MAIN, state: 'planned',
            title: 'Quote accepted', subtitle: 'From Inquiry to accepted quote',
            what: 'Starts when a customer accepts a quote: from the Inquiry to accepted quote workflow, or when someone marks a quote as won in Monada.',
        },
        {
            id: 'booking', kind: 'agent', x: col(1), y: MAIN, state: 'partial',
            title: 'Prepare the booking', subtitle: 'Missing details, hand to operations',
            what: 'Locks the agreed price, asks the customer for what booking needs (cargo ready date, pick-up contact, shipper and consignee, payment terms) and prepares the handoff to operations.',
        },
        {
            id: 'cargowise', kind: 'external', x: col(1), y: ROW2, state: 'built',
            title: 'CargoWise', subtitle: 'Operations system',
            what: 'Where operations work. Won quotes and shipments are sent to it.',
        },
        {
            id: 'coordinate', kind: 'agent', x: col(2), y: MAIN, state: 'planned',
            title: 'Coordinate', subtitle: 'Customer, agent, trucker, warehouse',
            what: 'Puts the parties in touch on the threads where each conversation already is: the agent\'s or trucker\'s contacts to the customer, the shipper\'s or consignee\'s to the agent.',
        },
        {
            id: 'teams', kind: 'external', icon: faMicrosoft, x: col(2), y: ROW2, state: 'partial',
            title: 'Elariom in Teams', subtitle: 'Where your team talks to it',
            what: 'Questions and approvals for the booking happen in your Teams chat with Elariom. Monada shows the whole job.',
        },
        {
            id: 'arrival', kind: 'agent', x: col(3), y: MAIN, state: 'planned',
            title: 'Arrival & completion', subtitle: 'Notice of arrival, updates, delivered',
            what: 'Sends the notice of arrival when the vessel is close (a new thread, or a reply on the original one), updates during the shipment, and closes the job when the voyage is complete.',
        },
        {
            id: 'job', kind: 'knowledge', x: col(3), y: ROW2, state: 'planned',
            title: 'The job', subtitle: 'Every thread and record of one shipment',
            what: 'Everything about one shipment in one place, carried over from the quote: the customer\'s emails, the agents\' replies, the booking, the arrival notice and the shipment.',
        },
    ].map(n => ({ ...n, planned: n.state === 'planned' }));

    const edges = [
        { from: 'accepted', to: 'booking' },
        { from: 'booking', to: 'cargowise' },
        { from: 'booking', to: 'coordinate' },
        { from: 'coordinate', to: 'teams' },
        { from: 'coordinate', to: 'arrival' },
        { from: 'arrival', to: 'job' },
    ].map(e => ({ label: null, route: 'straight', ...e }));

    return {
        id: 'booking-to-voyage-completion',
        name: 'Booking to voyage completion',
        preview: true,
        description: 'From an accepted quote to a completed voyage: prepares the booking and hands it to operations, keeps the customer, agents, truckers and warehouses in touch, and sends the arrival notice and updates until delivery.',
        trigger: 'A quote is accepted',
        width: col(4),
        height: ROW2 + 120,
        nodeWidth: W,
        nodes,
        edges,
    };
}

// Workflow #4: an RFQ from request to the best offer (docs/air-agent/platform-plan.md, "Workflow #4").
// A preview: built or partial steps are available today, planned ones are coming soon.
export function rfqToBestOfferWorkflow() {
    const W = 200;
    const GAP = 52;
    const col = n => 24 + n * (W + GAP);
    const MAIN = 80;
    const ROW2 = 250;
    const ROW3 = 400;

    const nodes = [
        {
            id: 'request', kind: 'trigger', icon: faEnvelope, x: col(0), y: 16, state: 'planned',
            title: 'Request or PO arrives', subtitle: 'By email: a customer\'s order or request',
            what: 'A purchase order or a request for rates reaches a connected mailbox: a shipper\'s PO, a customer asking for a campaign or tender price, or a colleague asking for costs.',
        },
        {
            id: 'chat', kind: 'trigger', icon: faMicrosoft, x: col(0), y: 144, state: 'partial',
            title: 'Asked in Teams', subtitle: '"Get me rates for…"',
            what: 'Someone asks Elariom in Teams to get rates for a shipment or a list of lanes.',
        },
        {
            id: 'fromQuote', kind: 'trigger', icon: faFileLines, x: col(0), y: 272, state: 'planned',
            title: 'Legs without a price', subtitle: 'From Inquiry to accepted quote',
            what: 'While a quote is priced, the legs that have no good rate in your rates or on the platforms start an RFQ here.',
        },
        {
            id: 'understand', kind: 'agent', x: col(1), y: MAIN, state: 'partial',
            title: 'Understand the request', subtitle: 'Lanes, equipment, cargo, dates, terms',
            what: 'Reads the request into lanes and legs: origin and destination (port, or door and ZIP), equipment and quantities, commodity, dangerous or reefer, ready dates, Incoterm per lane (DAP or CFR changes what each price must include), the account it\'s for, and the deadline.',
        },
        {
            id: 'complete', kind: 'condition', x: col(2), y: MAIN, state: 'planned',
            title: 'Complete?', subtitle: 'Enough for a supplier to price',
            what: 'Checks the request has what a supplier needs to price it without coming back with questions: weights for LCL and air, the MSDS for dangerous cargo, a temperature for reefer, pick-up or delivery addresses.',
        },
        {
            id: 'askRequester', kind: 'human', x: col(2), y: ROW2, state: 'planned',
            title: 'Ask what\'s missing', subtitle: 'One question at a time',
            what: 'Asks the requester for what is missing, one question at a time, and continues when the answer arrives.',
        },
        {
            id: 'choose', kind: 'agent', x: col(3), y: MAIN, state: 'planned',
            title: 'Choose who to ask', subtitle: 'Coverage, scores, promised work',
            what: 'Picks the carriers, overseas agents and local suppliers for each lane: who covers that country and service, who answers fast and wins often, and who was promised work. Suggests asking a few at once so nobody waits on one reply.',
        },
        {
            id: 'scores', kind: 'knowledge', x: col(3), y: ROW2, state: 'partial',
            title: 'Suppliers and scores', subtitle: 'Who covers what, how they do',
            what: 'Your suppliers and agents, the countries and services they cover, and how they have done: response rate and time, how often they win, price against others, and any promise of work.',
        },
        {
            id: 'discover', kind: 'external', x: col(3), y: ROW3, state: 'planned',
            title: 'Find someone new', subtitle: 'Networks, directories, groups',
            what: 'For a country you have never worked with: forwarder networks and directories, and later your WhatsApp groups. Anything posted in a group is approved first and never names the customer or prices.',
        },
        {
            id: 'draft', kind: 'agent', x: col(4), y: MAIN, state: 'partial',
            title: 'Draft the RFQs', subtitle: 'Complete, urgent, customer hidden',
            what: 'Writes one RFQ per supplier in their language: every lane with all the details, the deadline and how urgent it is, rephrased so the supplier doesn\'t see who the customer is. The reference in the subject links every reply back to this request.',
        },
        {
            id: 'send', kind: 'human', x: col(5), y: MAIN, state: 'partial',
            title: 'Approve & send', subtitle: 'In Teams, sent from Monada',
            what: 'You approve the list of suppliers and the RFQs in Teams, or change them. They go out from Monada, so every reply comes back on a thread Monada already knows.',
        },
        {
            id: 'suppliers', kind: 'external', x: col(5), y: ROW3, state: 'partial',
            title: 'Carriers, agents, suppliers', subtitle: 'Email · WhatsApp later',
            what: 'Carriers you have contracts with, overseas agents and local suppliers, reached by email.',
        },
        {
            id: 'replies', kind: 'agent', x: col(6), y: MAIN, state: 'partial',
            title: 'Read the replies', subtitle: 'Emails, PDFs, tables',
            what: 'Reads each reply, in the email body or attached PDFs, from the newest message only (older offers in the thread are history). Every option becomes a rate: supplier and carrier, routing, transit, free days, validity, every charge with its currency and basis. "Included" is noted, per-BL charges stay per shipment, and the supplier\'s own totals are used as a check.',
        },
        {
            id: 'followup', kind: 'agent', x: col(6), y: ROW2, state: 'planned',
            title: 'Follow up', subtitle: 'Who hasn\'t answered',
            what: 'Reminds suppliers who haven\'t answered before the deadline, asks the ones who answered only some lanes for the rest, and suggests an alternative supplier when one stays silent.',
        },
        {
            id: 'coverage', kind: 'code', x: col(7), y: MAIN, state: 'planned',
            title: 'Offers per lane', subtitle: 'What came in, what\'s missing',
            what: 'A table of every lane against the offers received: who answered which lanes, which lanes have no offer yet, and what is still out.',
        },
        {
            id: 'compare', kind: 'agent', x: col(8), y: MAIN, state: 'partial',
            title: 'Compare & recommend', subtitle: 'Price, transit, validity, reliability',
            what: 'Puts the options side by side in one currency (your exchange rates): total and breakdown, transit time, routing, validity, carrier and supplier, and how reliable the supplier has been. Recommends the cheapest, the fastest and the best value, and says why.',
        },
        {
            id: 'provisional', kind: 'knowledge', x: col(8), y: ROW2, state: 'planned',
            title: 'Recent offers', subtitle: 'While replies are still out',
            what: 'If a supplier answered a similar request in the last days, that offer is shown as provisional, so a price can go out the same day and be confirmed when the new reply arrives.',
        },
        {
            id: 'decide', kind: 'human', x: col(9), y: MAIN, state: 'planned',
            title: 'Choose the best', subtitle: 'Your call, in Teams',
            what: 'You choose the option per lane, or let Elariom take its recommendation.',
        },
        {
            id: 'close', kind: 'action', x: col(10), y: MAIN, state: 'partial',
            title: 'Save & close the loop', subtitle: 'Offers kept, suppliers told',
            what: 'Saves the offers on the job: the chosen ones go to the quote, and contract or tender rates can be published as rates. Tells each supplier whether they won, and why not when they lost, so they price better next time.',
        },
        {
            id: 'quote', kind: 'external', x: col(10), y: ROW2, state: 'planned',
            title: 'Back to the quote', subtitle: 'Inquiry to accepted quote',
            what: 'When the RFQ started from a quote, the chosen offers go back to its Collect & compare step.',
        },
    ].map(n => ({ ...n, planned: n.state === 'planned' }));

    const edges = [
        { from: 'request', to: 'understand' },
        { from: 'chat', to: 'understand' },
        { from: 'fromQuote', to: 'understand' },
        { from: 'understand', to: 'complete' },
        { from: 'complete', to: 'askRequester', label: 'missing' },
        { from: 'askRequester', to: 'understand', label: 'answers', route: 'left' },
        { from: 'complete', to: 'choose', label: 'yes' },
        { from: 'choose', to: 'scores' },
        { from: 'scores', to: 'discover', label: 'no one fits' },
        { from: 'choose', to: 'draft' },
        { from: 'draft', to: 'send' },
        { from: 'send', to: 'suppliers' },
        { from: 'send', to: 'replies', label: 'sent' },
        { from: 'replies', to: 'followup', label: 'not all in' },
        { from: 'followup', to: 'suppliers', label: 'reminders', route: 'downLeft' },
        { from: 'replies', to: 'coverage' },
        { from: 'coverage', to: 'compare' },
        { from: 'compare', to: 'provisional' },
        { from: 'compare', to: 'decide' },
        { from: 'decide', to: 'close', label: 'chosen' },
        { from: 'close', to: 'quote' },
    ].map(e => ({ label: null, route: 'straight', ...e }));

    return {
        id: 'rfq-to-best-offer',
        name: 'RFQ to best offer',
        preview: true,
        description: 'From a request or purchase order to the best offer: works out what to ask and who to ask, sends complete RFQs, reads the replies and follows up with whoever hasn\'t answered, then compares the options so you can choose the best and tell the suppliers.',
        trigger: 'A request or PO by email, a question in Teams, or legs a quote couldn\'t price',
        width: col(11),
        height: ROW3 + 120,
        nodeWidth: W,
        nodes,
        edges,
    };
}

export const MONADA_APPS = [
    { title: 'Quotes', path: '/opportunities', icon: faFileLines, detail: 'Requests, pricing, sending and follow-ups.' },
    { title: 'Rates', path: '/rates', icon: faFileContract, detail: 'Supplier rates, tariffs and imports.' },
    { title: 'Accounts', path: '/accounts', icon: faDatabase, detail: 'Customers, trade lines and activity.' },
    { title: 'Suppliers', path: '/suppliers', icon: faBoxesStacked, detail: 'Supplier records and their rate forms.' },
    { title: 'Bookings', path: '/bookings', icon: faShip, detail: 'Won shipments and their legs.' },
    { title: 'Analytics', path: '/quotation-analytics', icon: faCoins, detail: 'Quotation and sales performance.' },
];

// File formats Monada converts without the agent (src/server/rateAgent/converters.js).
export const CONVERTER_TITLES = {
    'air-template': 'air rate table converter',
    'metro-tariff': 'metro trucking sheet converter',
    'lcl-tiers': 'LCL tariff converter (CBM bands)',
};
export const CONVERTER_FORMATS = {
    'air-template': { name: 'an air rate table with weight breaks (one row per lane, airport codes, minimum and per-kg breaks)', page: 'a script that builds the weight-break formula' },
    'metro-tariff': { name: 'a metro trucking sheet (per-suburb rates from a port or airport)', page: 'Admin → Metro Tariff' },
    'lcl-tiers': { name: 'an LCL tariff with CBM bands', page: 'Admin → LCL Tariff Tiers' },
    'cma-quote': { name: 'a CMA CGM quotation export (Summary, Standard charges, Feeder tariff book)', page: 'a script that builds every route, feeders included' },
};
